import Tabs from "../../components/General/ResponsiveTabs/ResponsiveTabs";
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Tab,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { Outlet, useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchCustomerAssets } from "../../api/rentals";
import { formatDate } from "../../helpers/formatingFunctions";
import DownloadButton from "../../components/Fleet/DownloadButton/DownloadButton";
import useAuth from "../../hooks/useAuth";
import axios from "axios";
import { apiVariable } from "../../api/apiVariable";

const isRegistration = (t: string) => /registration/i.test(t);
const isLease = (t: string) => /(lease|agreement)/i.test(t);

const regFile = (files: any[] = []) =>
  files.find((f) => isRegistration(f.type));
const leaseFile = (files: any[] = []) => files.find((f) => isLease(f.type));

const fetchOwnerCompany = async (
  token: string | null | undefined,
  companyId: string,
) => {
  const guidPart = companyId.split("-").slice(0, 5).join("-");
  const response = await axios.get(`${apiVariable}/ownerCompany/${guidPart}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const CompanyUnits: React.FC = () => {
  const { companyId: customerId, unitId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [filter, setFilter] = useState<"all" | "truck" | "trailer">("all");

  const { data: ownerCompany, isLoading: isLoadingOwner } = useQuery({
    queryKey: ["ownerCompany", customerId],
    queryFn: () => fetchOwnerCompany(token, customerId || ""),
    enabled: !!customerId && !!token,
  });

  const { data: assets, isLoading: isLoadingAssets } = useQuery({
    queryKey: ["assets", customerId],
    queryFn: () => fetchCustomerAssets(token, customerId || ""),
    enabled: !!customerId,
  });

  if (isLoadingAssets || isLoadingOwner)
    return (
      <Container sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  if (!assets) return null;

  const rows = assets
    .filter((a: any) =>
      filter === "all" ? true : (a.unitType ?? "").toLowerCase() === filter,
    )
    .map((r: any) => ({
      id: r.id ?? `${r.unitType}-${r.unitNumber}`,
      customerId: r.customerId,
      ...r,
    }));

  const cols: GridColDef[] = [
    { field: "unitNumber", headerName: "Unit #", flex: 1 },
    { field: "unitType", headerName: "Type", flex: 0.7 },

    {
      field: "ownerCompanyName",
      headerName: "Company",
      flex: 1,
    },

    { field: "make", headerName: "Make", flex: 1 },
    { field: "model", headerName: "Model", flex: 1 },
    { field: "year", headerName: "Year", flex: 1 },
    { field: "licensePlate", headerName: "Plate", flex: 1 },
    { field: "vin", headerName: "VIN", flex: 2 },
    { field: "rentalTerm", headerName: "Rental Term", flex: 1 },
    { field: "billingFrequency", headerName: "Billing Freq.", flex: 1 },
    {
      field: "baseRentalRate",
      headerName: "Base Rate",
      flex: 1,
    },
    {
      field: "startDate",
      headerName: "Start Date",
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        formatDate(params.value ?? null),
    },
    {
      field: "endDate",
      headerName: "End Date",
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? formatDate(params.value) : "N/A",
    },
    {
      field: "downloadRegistration",
      headerName: "Registration",
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }: { row: any }) => {
        const file = regFile(row.assetFiles);
        return file ? (
          <Box onClick={(e) => e.stopPropagation()}>
            <DownloadButton
              params={{
                row: {
                  id: file.id,
                  fileName: file.fileName,
                  unitNumber: row.unitNumber,
                },
              }}
              type="assetFolder"
            />
          </Box>
        ) : null;
      },
    },
    {
      field: "downloadLease",
      headerName: "Lease Agr.",
      width: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }: { row: any }) => {
        const file = leaseFile(row.assetFiles);
        return file ? (
          <Box onClick={(e) => e.stopPropagation()}>
            <DownloadButton
              params={{
                row: {
                  id: file.id,
                  fileName: file.fileName,
                  unitNumber: row.unitNumber,
                },
              }}
              type="assetFolder"
            />
          </Box>
        ) : null;
      },
    },
  ];

  return (
    <>
      {unitId ? (
        <Outlet />
      ) : (
        <Container maxWidth={false} sx={{ py: 6 }}>
          <Tabs
            value={filter}
            onChange={(_, v) => setFilter(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{ mb: 3 }}
          >
            <Tab value="all" label="All Assets" />
            <Tab value="truck" label="Trucks" />
            <Tab value="trailer" label="Trailers" />
          </Tabs>

          <Typography variant="h4" gutterBottom>
            Assets rented by {ownerCompany?.name ?? "Loading..."}
          </Typography>

          <Box sx={{ height: 440, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={cols}
              disableRowSelectionOnClick
              onCellClick={(params) => {
                if (params.field === "unitNumber") {
                  const unitType = params.row.unitType?.toLowerCase();
                  if (unitType === "truck") {
                    navigate(`/fleet/assets/truck/${params.row.id}`);
                  } else if (unitType === "trailer") {
                    navigate(`/fleet/assets/trailer/${params.row.id}`);
                  }
                }
              }}
              slots={{
                toolbar: () => (
                  <GridToolbarQuickFilter
                    slotProps={{ root: { placeholder: "Search…" } }}
                  />
                ),
              }}
            />
          </Box>
        </Container>
      )}
    </>
  );
};
